// hooks/useTabManagement.js - CORREÇÃO COMPLETA DA VALIDAÇÃO
import { ref, computed, nextTick, watch } from 'vue';
import { useStore } from 'vuex';

export function useTabManagement() {
  const store = useStore();

  // Tab configuration
  const tabs = [
    {
      id: 'table',
      label: 'Configurar Público',
      shortLabel: 'Público',
      icon: '📊',
      title: 'Configurar Público',
      description: 'Selecione a tabela de dados e configure as colunas',
    },
    {
      id: 'placeholders',
      label: 'Mapear Placeholders',
      shortLabel: 'Placeholders',
      icon: '🔗',
      title: 'Mapear Placeholders',
      description:
        'Conecte os placeholders do template com as colunas da tabela',
    },
    {
      id: 'filters',
      label: 'Aplicar Filtros',
      shortLabel: 'Filtros',
      icon: '🔍',
      title: 'Aplicar Filtros',
      description: 'Refine seu público com filtros personalizados (opcional)',
    },
  ];

  // 🔧 CORREÇÃO PRINCIPAL: Estado centralizado e consistente
  const activeTab = ref('table');

  // 🔧 ESTADO UNIFICADO: Todas as tabs começam como não validadas
  // O check só aparece quando o usuário completa cada etapa
  const tabValidation = ref({
    table: false,
    placeholders: false,
    filters: false, // 🔧 MUDANÇA: Agora começa como false
  });

  // 🔧 NOVO: Controlar quais tabs já foram visitadas/completadas
  const tabsCompleted = ref({
    table: false,
    placeholders: false,
    filters: false,
  });

  // 🔧 CORREÇÃO: Propriedades computadas derivadas do estado central
  const isTableTabValid = computed({
    get: () => tabValidation.value.table,
    set: value => {
      tabValidation.value.table = value;
      // Marcar como completada quando validada
      if (value) {
        tabsCompleted.value.table = true;
      }
    },
  });

  const isPlaceholdersValid = computed({
    get: () => tabValidation.value.placeholders,
    set: value => {
      tabValidation.value.placeholders = value;
      // Marcar como completada quando validada
      if (value) {
        tabsCompleted.value.placeholders = true;
      }
    },
  });

  // 🔧 NOVO: Computed para mostrar se deve exibir o check
  const shouldShowCheck = computed(() => tabId => {
    // Para filters, mostrar check apenas se foi visitada E tem filtros aplicados
    // OU se não tem filtros mas foi explicitamente validada
    if (tabId === 'filters') {
      return tabsCompleted.value.filters || tabValidation.value.filters;
    }

    // Para outras tabs, mostrar check apenas se foram validadas
    return tabValidation.value[tabId];
  });

  // 🔧 CORREÇÃO: Função centralizada para atualizar validação
  const updateValidation = (tabId, isValid) => {
    if (tabId === 'table') {
      tabValidation.value.table = Boolean(isValid);
      if (isValid) {
        tabsCompleted.value.table = true;
      }
    } else if (tabId === 'placeholders') {
      tabValidation.value.placeholders = Boolean(isValid);
      if (isValid) {
        tabsCompleted.value.placeholders = true;
      }
    } else if (tabId === 'filters') {
      tabValidation.value.filters = Boolean(isValid);
      tabsCompleted.value.filters = true; // Filters sempre marcam como visitada
    }
  };

  // 🔧 NOVO: Função para marcar tab como visitada (independente de validação)
  const markTabAsVisited = tabId => {
    tabsCompleted.value[tabId] = true;

    // Para filters, marcar como válida também (já que é opcional)
    if (tabId === 'filters') {
      tabValidation.value.filters = true;
    }
  };

  // 🔧 CORREÇÃO: Função de sincronização simplificada
  const syncValidation = () => {
    return tabValidation.value;
  };

  const currentTabData = computed(() => {
    return tabs.find(tab => tab.id === activeTab.value);
  });

  const canSubmit = computed(() => {
    // Para submeter, precisa ter table e placeholders válidos
    // Filters é opcional, então não é obrigatório para submit
    const result =
      tabValidation.value.table && tabValidation.value.placeholders;

    return result;
  });

  // 🔧 CORREÇÃO: Navegação de tabs com validação sequencial rigorosa
  const canNavigateToTab = tabId => {
    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);

    // Sempre pode voltar para tabs anteriores
    if (tabIndex <= currentIndex) {
      return true;
    }

    // Para avançar, precisa ter completado as tabs anteriores
    if (tabId === 'placeholders') {
      return tabValidation.value.table;
    }

    if (tabId === 'filters') {
      return tabValidation.value.table && tabValidation.value.placeholders;
    }

    return true;
  };

  const onTabChange = async (tabId, validateCurrentTabFn, filtersRef) => {
    // 🔧 CORREÇÃO: Verificar se pode navegar ANTES de qualquer validação
    if (!canNavigateToTab(tabId)) {
      return false;
    }

    // 🔧 CORREÇÃO: Só validar se estiver indo para frente
    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);

    if (tabIndex > currentIndex && validateCurrentTabFn) {
      const isValid = await validateCurrentTabFn();
      if (!isValid) {
        return false;
      }
    }

    activeTab.value = tabId;

    // 🔧 NOVO: Marcar tab como visitada quando entra nela
    markTabAsVisited(tabId);

    // Quando mudar para filters, restaurar do Vuex
    if (tabId === 'filters') {
      await nextTick();

      const currentData = store.getters['campaigns/getCampaignData'];

      // Se há filtros salvos no Vuex, restaurar
      if (
        currentData.filters &&
        currentData.filters.length > 0 &&
        filtersRef?.value
      ) {
        // Atualizar filtros diretamente
        if (filtersRef.value.updateFilters) {
          filtersRef.value.updateFilters(currentData.filters);
        }

        // Persistir estado
        if (filtersRef.value.persistFilters) {
          filtersRef.value.persistFilters();
        }

        // Forçar atualização visual
        if (filtersRef.value.forceUpdate) {
          filtersRef.value.forceUpdate();
        }
      }
    }

    return true;
  };

  const nextTab = async validateCurrentTabFn => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
    if (currentIndex >= tabs.length - 1) {
      return;
    }

    const nextTabId = tabs[currentIndex + 1].id;

    // Validar tab atual antes de prosseguir
    if (validateCurrentTabFn) {
      const isValid = await validateCurrentTabFn();
      if (!isValid) {
        return;
      }
    }

    if (canNavigateToTab(nextTabId)) {
      activeTab.value = nextTabId;
      markTabAsVisited(nextTabId);
    }
  };

  const previousTab = async () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
    if (currentIndex <= 0) {
      return;
    }

    const prevTabId = tabs[currentIndex - 1].id;
    activeTab.value = prevTabId;
  };

  // 🔧 EXPOSE: Todas as propriedades e métodos necessários
  return {
    // Configuração
    tabs,

    // Estado
    activeTab,
    tabValidation,
    tabsCompleted,

    // Computed properties
    isTableTabValid,
    isPlaceholdersValid,
    currentTabData,
    canSubmit,
    shouldShowCheck,

    // Métodos de navegação
    canNavigateToTab,
    onTabChange,
    nextTab,
    previousTab,

    // Métodos de validação
    updateValidation,
    syncValidation,
    markTabAsVisited,
  };
}
